import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AgentLayout from "../components/layout/AgentLayout";
import PageHeader from "../components/common/PageHeader";
import { DASHBOARD_CRUMB } from "../components/common/Breadcrumb";
import { agentApi } from "../lib/apiClient";
import { showApiError } from "../lib/alerts";
import { asArray, unwrapData, appendUniqueById, readPagination } from "../lib/catalog";
import { CatalogSkeletonGrid } from "../components/catalog/CatalogSkeletons";
import FlightCard from "../components/flights/FlightCard";

const PAGE_SIZE = 16;

function normalizeBlocks(payload) {
  const data = unwrapData(payload);
  return asArray(data).map((item) => ({
    id: item.id || item.fb_id,
    code: item.block_code || item.fb_code,
    name: item.block_name || item.fb_name,
    status: item.status || item.fb_status,
    seats: item.available_seats ?? item.fb_seat,
    adultPrice: item.adult_price ?? item.fb_adult_price,
    childPrice: item.child_price ?? item.fb_child_price,
    infantPrice: item.infant_price ?? item.fb_infant_price,
    pnr: item.pnr || item.fb_pnr,
  })).filter((item) => item.id);
}

function matchesFlightSearch(item, search) {
  const query = search.trim().toLowerCase();
  if (!query) return true;
  return [item.name, item.code, item.status, item.pnr].some((value) =>
    String(value || "").toLowerCase().includes(query)
  );
}

export default function FlightsPage() {
  const [searchParams] = useSearchParams();
  const [blocks, setBlocks] = useState([]);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [total, setTotal] = useState(0);
  const [endpoint, setEndpoint] = useState("/flight/blocks/active");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [appliedSearch, setAppliedSearch] = useState(searchParams.get("q") || "");

  function flightQueryParams(pageNumber) {
    return {
      page: pageNumber,
      limit: PAGE_SIZE,
      search: appliedSearch.trim() || undefined,
    };
  }

  useEffect(() => {
    let alive = true;

    async function loadBlocks() {
      setLoading(true);
      try {
        let nextEndpoint = endpoint || "/flight/blocks/active";
        let payload;
        try {
          payload = await agentApi.get("/flight/blocks/active", {
            params: flightQueryParams(1),
          });
          nextEndpoint = "/flight/blocks/active";
        } catch (activeError) {
          if (activeError.status !== 404) throw activeError;
          nextEndpoint = "/flight/blocks";
          payload = await agentApi.get(nextEndpoint, {
            params: flightQueryParams(1),
          });
        }
        if (!alive) return;
        const items = normalizeBlocks(payload);
        const pagination = readPagination(payload);
        setEndpoint(nextEndpoint);
        setBlocks(items);
        setPage(1);
        setHasNext(pagination.hasNext);
        setTotal(pagination.total || items.length);
      } catch (fetchError) {
        if (!alive) return;
        setBlocks([]);
        setHasNext(false);
        showApiError(fetchError, "LOAD_FLIGHTS");
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadBlocks();

    return () => {
      alive = false;
    };
  }, [appliedSearch]);

  useEffect(() => {
    const nextQuery = searchParams.get("q") || "";
    setSearch(nextQuery);
    setAppliedSearch(nextQuery);
  }, [searchParams]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setAppliedSearch((current) => {
        const next = search.trim();
        return current === next ? current : next;
      });
    }, 400);

    return () => window.clearTimeout(handle);
  }, [search]);

  function applySearch() {
    setAppliedSearch(search.trim());
  }

  async function loadMore() {
    const nextPage = page + 1;
    setLoadingMore(true);
    try {
      const payload = await agentApi.get(endpoint, {
        params: flightQueryParams(nextPage),
      });
      const items = normalizeBlocks(payload);
      const pagination = readPagination(payload);
      setBlocks((current) => appendUniqueById(current, items, (item) => item.id));
      setPage(nextPage);
      setHasNext(pagination.hasNext);
      setTotal(pagination.total || 0);
    } catch (fetchError) {
      showApiError(fetchError, "LOAD_FLIGHTS");
    } finally {
      setLoadingMore(false);
    }
  }

  const displayedBlocks = useMemo(
    () => blocks.filter((item) => matchesFlightSearch(item, search)),
    [blocks, search]
  );

  return (
    <AgentLayout>
      <div className="container-fluid py-24 package-catalog-page">
        <PageHeader
          title="Umrah Flights"
          items={[DASHBOARD_CRUMB, { label: "Services" }, { label: "Flights" }]}
        >
          <div className="package-filter-bar package-filter-bar--flights" role="search">
            <div className="package-filter-bar__field package-filter-bar__search">
              <label className="package-filter-bar__label" htmlFor="flight-filter-search">Search</label>
              <input
                id="flight-filter-search"
                type="search"
                className="form-control"
                placeholder="Search flight blocks"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                onBlur={applySearch}
                onKeyDown={(event) => {
                  if (event.key === "Enter") applySearch();
                }}
              />
            </div>
          </div>
        </PageHeader>

        {loading ? <CatalogSkeletonGrid variant="flight" count={8} label="Loading live flights..." /> : null}

        {!loading && displayedBlocks.length === 0 ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-5">
              <h5 className="mb-2">No flight blocks found</h5>
              <p className="text-secondary mb-0">
                {search.trim()
                  ? "Try a different search term."
                  : "Try a different search term or refresh after flight blocks are published in admin."}
              </p>
            </div>
          </div>
        ) : null}

        {!loading && displayedBlocks.length > 0 ? (
          <div className="package-catalog-grid">
            {displayedBlocks.map((item) => (
              <FlightCard key={item.id} item={item} />
            ))}
          </div>
        ) : null}

        {!loading && hasNext ? (
          <div className="text-center mt-4">
            <button type="button" className="btn btn-outline-primary" disabled={loadingMore} onClick={loadMore}>
              {loadingMore ? "Loading..." : `Load more flights${total ? ` (${blocks.length}/${total})` : ""}`}
            </button>
          </div>
        ) : null}
      </div>
    </AgentLayout>
  );
}
